'use client'

import { useMutation } from '@tanstack/react-query'
import { ChevronDown } from 'lucide-react'

import type { SchoolGrade } from '@helper/utils/domain'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  toast,
} from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import { SCHOOL_GRADE_OPTIONS, schoolGradeLabel } from '@helper/utils/domain'
import cn from '@config/tailwind/cn'

import type { Student } from '../types'
import { useSharedTRPC } from '../../../context'
import { useStudentMutations } from '../use-student-mutations'

/** Base UI radio groups cannot hold `null`, so "no grade" rides on a sentinel. */
const NO_GRADE_VALUE = '__none__'

interface GradeCellProps {
  student: Student
  canEdit: boolean
}

/** Grade level (`user.schoolClass`) — the "Tingkat" column, distinct from the class group. */
export function GradeCell({ student, canEdit }: GradeCellProps) {
  const trpc = useSharedTRPC()
  const { invalidate } = useStudentMutations()

  const editChild = useMutation(trpc.myOrganization.editChild.mutationOptions())

  const currentGrade = student.schoolGrade ?? null
  const label = schoolGradeLabel(currentGrade) ?? '-'

  const handleChange = async (value: string) => {
    const nextGrade = value === NO_GRADE_VALUE ? null : (value as SchoolGrade)
    if (nextGrade === currentGrade) return

    try {
      await editChild.mutateAsync({ userId: student.id, data: { schoolClass: nextGrade } })
      await invalidate()
      toast.success(`Tingkat ${student.name} berhasil diperbarui.`)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Gagal memperbarui tingkat')
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        disabled={!canEdit || editChild.isPending}
        render={
          <button
            type='button'
            className={cn(
              typographyVariants({ variant: 'body-md' }),
              'srd:text-neutral-black srd:flex srd:w-full srd:min-w-[110px] srd:items-center srd:justify-between srd:gap-x-2 srd:text-nowrap srd:disabled:cursor-not-allowed',
            )}
          >
            {label}
            <ChevronDown className='srd:text-grey-100 srd:size-4 srd:shrink-0' />
          </button>
        }
      />

      <DropdownMenuContent align='start' className='srd:max-h-[280px] srd:overflow-y-auto'>
        <DropdownMenuRadioGroup
          value={currentGrade ?? NO_GRADE_VALUE}
          onValueChange={(value) => void handleChange(value)}
        >
          <DropdownMenuRadioItem value={NO_GRADE_VALUE} className='srd:data-checked:bg-blue-10'>
            Tanpa tingkat
          </DropdownMenuRadioItem>

          <DropdownMenuSeparator />

          {SCHOOL_GRADE_OPTIONS.map((option) => (
            <DropdownMenuRadioItem key={option.value} value={option.value} className='srd:data-checked:bg-blue-10'>
              {option.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
